import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Plus, Search } from "lucide-react";

export const DEPT_LABEL = { fnb: "F&B", housekeeping: "Housekeeping", maintenance: "Συντήρηση" };
const DEPTS = Object.keys(DEPT_LABEL);
const inp = "rounded-lg border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]";

export const InventoryItems = ({ dept }) => {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [all, setAll] = useState(false);
  const [f, setF] = useState({ name: "", unit: "τεμ", category: "", price_per_unit: 0 });
  const { data: items = [] } = useQuery({ queryKey: ["inv-items"], queryFn: async () => (await api.get("/inventory/items")).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["inv-items"] }); qc.invalidateQueries({ queryKey: ["inv-report"] }); };
  const add = useMutation({
    mutationFn: async () => (await api.post("/inventory/items", { ...f, price_per_unit: Number(f.price_per_unit) || 0, departments: [dept] })).data,
    onSuccess: () => { refresh(); setF({ name: "", unit: "τεμ", category: "", price_per_unit: 0 }); toast.success("Το είδος προστέθηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const setDepts = useMutation({
    mutationFn: async ({ id, departments }) => api.put(`/inventory/items/${id}/departments`, { departments }),
    onSuccess: refresh,
    onError: (e) => toast.error(apiErr(e)),
  });
  const shown = items.filter((i) => (all || (i.departments || ["fnb"]).includes(dept)) && (!q || i.name.toLowerCase().includes(q.toLowerCase()))).slice(0, 200);
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] p-4 space-y-3" data-testid="inv-items-panel">
      <h2 className="font-medium text-[#45423C]">Είδη απογραφής — κοινή λίστα, επιλέξτε σε ποια τμήματα μετριέται κάθε είδος</h2>
      <div className="flex flex-wrap gap-2">
        <input data-testid="inv-item-name" className={inp} placeholder="Νέο είδος (π.χ. Σεντόνι διπλό)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <input data-testid="inv-item-unit" className={`${inp} w-24`} placeholder="Μονάδα" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} />
        <input data-testid="inv-item-category" className={`${inp} w-40`} placeholder="Κατηγορία" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
        <input data-testid="inv-item-price" type="number" className={`${inp} w-28`} placeholder="Τιμή €" value={f.price_per_unit} onChange={(e) => setF({ ...f, price_per_unit: e.target.value })} />
        <button data-testid="inv-item-add" disabled={!f.name.trim() || add.isPending} onClick={() => add.mutate()}
          className="inline-flex items-center gap-1.5 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold disabled:opacity-40"><Plus className="h-4 w-4" /> Στο {DEPT_LABEL[dept]}</button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-[#8A8577]" />
          <input data-testid="inv-item-search" className={`${inp} w-full pl-9`} placeholder="Αναζήτηση είδους" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-[#6F6A60]">
          <input type="checkbox" data-testid="inv-item-show-all" checked={all} onChange={(e) => setAll(e.target.checked)} />
          Όλα τα είδη (όχι μόνο {DEPT_LABEL[dept]})
        </label>
      </div>
      <div className="max-h-80 overflow-y-auto divide-y divide-[#F1EDE6] text-sm">
        {shown.map((i) => (
          <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5" data-testid={`inv-item-${i.id}`}>
            <span className="text-[#45423C]">{i.name} <span className="text-[#8A8577] text-xs">{i.category} · {i.unit}</span></span>
            <span className="flex gap-1">
              {DEPTS.map((d) => {
                const on = (i.departments || []).includes(d);
                return (
                  <button key={d} data-testid={`inv-item-${i.id}-${d}`} onClick={() => setDepts.mutate({ id: i.id, departments: on ? i.departments.filter((x) => x !== d) : [...(i.departments || []), d] })}
                    className={`px-2 py-0.5 rounded-full text-[11px] border ${on ? "bg-[#45423C] text-white border-[#45423C]" : "bg-white text-[#6F6A60] border-[#E3DED4]"}`}>{DEPT_LABEL[d]}</button>
                );
              })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
